import { useEffect, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import { FeedView } from '@/components/feed/FeedView';
import {
  FEED_SCOPE_COOKIE,
  parseScope,
} from '@/components/feed/feed-scope';
import {
  OnboardingChecklist,
  useOnboardingChecklistVisible,
} from '@/components/onboarding/OnboardingChecklist';
import { useComposerGroups } from '@/lib/queries/composer';
import { useFormatDate } from '@/lib/format-date.client';
import { timeOfDay } from '@/lib/viewer-time';

function getViewerTimeZoneClient(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}
import type { PostComposerHandle } from '@/components/feed/PostComposer';

type FeedSearch = { scope?: string; compose?: true };

export const Route = createFileRoute('/_authed/feed/')({
  validateSearch: (raw: Record<string, unknown>): FeedSearch => {
    const out: FeedSearch = {};
    if (typeof raw.scope === 'string' && raw.scope.length > 0) {
      out.scope = raw.scope;
    }
    // `?compose=1` (onboarding "Create a post" CTA, etc.) auto-opens the
    // composer on landing. Accept the truthy string/number/bool forms.
    if (raw.compose === '1' || raw.compose === 1 || raw.compose === true || raw.compose === 'true') {
      out.compose = true;
    }
    return out;
  },
  component: FeedPage,
});

function readScopeCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${FEED_SCOPE_COOKIE}=`));
  return m ? decodeURIComponent(m.split('=')[1] ?? '') : undefined;
}

function FeedPage() {
  const member = useMember();
  const search = Route.useSearch();
  const fmt = useFormatDate();
  const cookieScope = readScopeCookie();
  const scope = parseScope(search.scope ?? cookieScope);

  const now = new Date();
  const tz = getViewerTimeZoneClient();
  const tod = timeOfDay(now, tz);
  const today = fmt.weekday(now);

  const { data: myGroups } = useComposerGroups();

  const viewer = (() => {
    if (member === null) return null;
    const photo = memberPhotoUrl(member);
    return {
      id: member.id,
      name: `${member.firstName} ${member.lastName}`.trim(),
      ...(photo !== undefined ? { photoUrl: photo } : {}),
    };
  })();

  const composerHandle = useRef<PostComposerHandle>(null);

  // `?compose=1` deep links (e.g. the onboarding "Create a post" step) open
  // and focus the composer on landing. The ref is populated after FeedView
  // mounts the composer, so defer one frame before focusing + expanding.
  useEffect(() => {
    if (!search.compose) return;
    const id = requestAnimationFrame(() => composerHandle.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [search.compose]);

  const showOnboarding = useOnboardingChecklistVisible();
  const onboardingSlot = showOnboarding ? (
    <OnboardingChecklist viewerFirstName={member?.firstName} />
  ) : undefined;

  return (
    <FeedView
      today={today}
      timeOfDay={tod}
      viewer={viewer}
      defaultScope={scope}
      myGroups={myGroups}
      onboardingSlot={onboardingSlot}
      composerHandle={composerHandle}
    />
  );
}
